import { useState } from 'react';
import { ChevronDown, CheckCircle, Clock, Mail, FileText } from 'lucide-react';
import { colors } from '../../styles/colors';
import ModalPortal from '../ModalPortal';

const STATUTS = ['Brouillon', 'A envoyer', 'Emise', 'Payée'];

const statutColors = {
  'Brouillon': { bg: colors.background.lightGray, text: colors.text.gray, icon: Clock },
  'A envoyer': { bg: colors.background.paleYellow, text: colors.text.brown, icon: Mail },
  'Emise': { bg: colors.background.paleBlue, text: colors.text.darkBlue, icon: FileText },
  'Payée': { bg: colors.background.paleGreen, text: colors.text.darkGreen2, icon: CheckCircle }
};

/**
 * StatusDropdown - Badge de statut avec dropdown pour changer le statut
 *
 * @param {Object} props
 * @param {string} props.currentStatus - Statut actuel
 * @param {function} props.onStatusChange - Callback async (newStatus) => void
 * @param {boolean} props.canEdit - Peut éditer le statut
 * @param {boolean} props.disabled - Désactive le bouton
 */
function StatusDropdown({ currentStatus, onStatusChange, canEdit, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isUpdating, setIsUpdating] = useState(false);

  const statutStyle = statutColors[currentStatus] || statutColors['Brouillon'];
  const StatutIcon = statutStyle.icon;

  const handleClick = (e) => {
    if (!canEdit || disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
    setIsOpen(!isOpen);
  };

  const handleSelect = async (newStatus) => {
    if (newStatus === currentStatus || isUpdating) return;

    setIsUpdating(true);
    try {
      await onStatusChange(newStatus);
      setIsOpen(false);
    } catch (err) {
      console.error('Erreur lors du changement de statut:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const isDisabled = !canEdit || disabled;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleClick}
        disabled={isDisabled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.25rem 0.625rem',
          borderRadius: '1rem',
          backgroundColor: statutStyle.bg,
          color: statutStyle.text,
          fontSize: '0.75rem',
          fontWeight: '600',
          border: 'none',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          transition: 'all 150ms',
          opacity: isDisabled ? 0.6 : 1,
          fontFamily: '"Noto Sans", sans-serif'
        }}
        onMouseEnter={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
        title={
          !canEdit
            ? 'Seuls les super admins peuvent modifier le statut'
            : 'Cliquer pour changer le statut'
        }
      >
        <StatutIcon size={12} />
        {currentStatus}
        {canEdit && <ChevronDown size={12} style={{ opacity: 0.7 }} />}
      </button>

      {isOpen && (
        <ModalPortal>
          {/* Backdrop to close dropdown */}
          <div
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
          />

          {/* Dropdown menu */}
          <div
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              transform: 'translateX(-50%)',
              backgroundColor: colors.text.white,
              borderRadius: '0.5rem',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              border: `1px solid ${colors.secondary.lightGray}`,
              zIndex: 1000,
              minWidth: '140px',
              overflow: 'hidden'
            }}
          >
            {STATUTS.map((s) => {
              const sStyle = statutColors[s] || statutColors['Brouillon'];
              const SIcon = sStyle.icon;
              const isSelected = currentStatus === s;

              return (
                <button
                  key={s}
                  onClick={() => handleSelect(s)}
                  disabled={isUpdating}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.875rem',
                    border: 'none',
                    backgroundColor: isSelected ? sStyle.bg : 'transparent',
                    cursor: isUpdating ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.8125rem',
                    color: sStyle.text,
                    fontWeight: isSelected ? '600' : '400',
                    transition: 'background-color 150ms',
                    textAlign: 'left',
                    fontFamily: '"Noto Sans", sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = colors.secondary.lightGray;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <SIcon size={16} />
                  <span style={{ flex: 1 }}>{s}</span>
                  {isSelected && <CheckCircle size={14} />}
                </button>
              );
            })}
          </div>
        </ModalPortal>
      )}
    </div>
  );
}

export default StatusDropdown;
